/**
 * Report-library hydration policy.
 *
 * The library retains every immutable revision so history, exports, and the
 * report reader can address an exact artifact. The Findings cockpit, however,
 * consumes only the newest record for each site + report-type pair. Hydrating
 * every historical body on list load therefore spends tens of megabytes before
 * the first current answer can render without increasing Findings coverage.
 *
 * Keep historical rows as explicit metadata-only records; the report reader's
 * existing `hydrateReportBody` path fetches an exact historical body when a
 * user opens it. Current heads are fully hydrated before `isReportsLoaded`
 * becomes true, so settled Findings never masquerades partial coverage as
 * complete.
 */

import type { SiteRagReport } from '../types/siteRag';
import { reportBelongsToSite, reportSiteGroupKey } from './reportSiteIdentity';
import {
  isFullReaderReadyReportBody,
  isImmutableRevisionReport,
  isPortfolioSlimReportBody,
  mergeReportBodyWithAuthoritativeMetadata,
  pickAuthoritativeReportMetadata,
} from './reportBodyHydration';

export const REPORT_LIST_BODY_HYDRATION_CONCURRENCY = 6;
/**
 * The remaining portfolio heads load behind the loading gate, so they take
 * a narrower lane and leave bandwidth for the open site's own requests.
 */
export const REPORT_BACKGROUND_BODY_HYDRATION_CONCURRENCY = 2;
export const REPORT_BODY_READ_TIMEOUT_MS = 30_000;

/** A late list pass or slim background pass must not erase an exact full body opened in the meantime. */
export function retainOpenedReportBody(metadata: SiteRagReport, opened: SiteRagReport | undefined): SiteRagReport {
  const incomingIsIncomplete = metadata._metadataOnly === true || isPortfolioSlimReportBody(metadata);
  if (!incomingIsIncomplete || !opened || !isFullReaderReadyReportBody(opened)) return metadata;
  const next = metadata as unknown as Record<string, unknown>;
  const current = opened as unknown as Record<string, unknown>;
  if (!isImmutableRevisionReport(next) || typeof next.storagePath !== 'string' || !next.storagePath
    || ['id', 'siteId', 'storagePath', 'reportRevisionId', 'sourceHash'].some(key => next[key] !== current[key])) return metadata;
  return mergeReportBodyWithAuthoritativeMetadata(opened, next) ?? metadata;
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function reportTimestamp(report: Record<string, unknown>): number {
  const raw = stringValue(report.updatedAt) ?? stringValue(report.createdAt);
  const parsed = raw ? Date.parse(raw) : Number.NaN;
  return Number.isFinite(parsed) ? parsed : 0;
}

function reportScopeKey(report: Record<string, unknown>): string {
  // No trustworthy site identity: isolate the row so optimization can never
  // suppress the only body for an otherwise ungroupable report.
  return reportSiteGroupKey({ id: report.id, siteId: report.siteId, siteName: report.siteName });
}

function reportTypeKey(report: Record<string, unknown>): string {
  return stringValue(report.reportType) ?? 'feasibility';
}

/**
 * Metadata-only approximation of the Findings evidence gate.
 *
 * Firestore report rows carry these delivery fields even when the immutable
 * body stays in object storage. That is enough to distinguish a passing
 * carrier from a newer held/draft/degraded attempt before hydration. When a
 * semantic-readiness envelope is present, mirror the brief's positive states;
 * its exact schema and identity are still checked authoritatively after load.
 */
function canSupplyBriefFromMetadata(report: Record<string, unknown>): boolean {
  const holdFindings = report.qualityHoldFindings;
  const revision = report.analysisRevision != null
    && typeof report.analysisRevision === 'object'
    && !Array.isArray(report.analysisRevision)
      ? report.analysisRevision as Record<string, unknown>
      : null;
  const rawReadiness = report.analysisReadiness ?? revision?.readiness;
  if (rawReadiness != null) {
    if (typeof rawReadiness !== 'object' || Array.isArray(rawReadiness)) return false;
    const readiness = rawReadiness as Record<string, unknown>;
    if (
      readiness.ready === true
      && readiness.canUseForDecision === true
      && readiness.current === true
    ) {
      return true;
    }
    return (
      (readiness.state === 'queued' || readiness.state === 'refreshing')
      && readiness.canView === true
      && readiness.identityValid === true
      && readiness.qualityReady === true
      && readiness.narrativeReady === true
    );
  }
  return (
    report.requiresRegeneration == null
    && report.degradedMode !== true
    && report.generationTier !== 'draft'
    && report.isDraft !== true
    && report.qualityHold !== 'needs-revision'
    && (!Array.isArray(holdFindings) || holdFindings.length === 0)
  );
}

/**
 * Metadata-only approximation of the saved-result calculation gate.
 *
 * Currency overlays are intentionally ignored: a stale successful revision
 * still needs its body hydrated so it can remain the calculation fallback.
 * Immutable identity, private access, finalization, and body availability stay
 * fail-closed and are revalidated against the full body after hydration.
 */
function canSupplySavedCalculationFromMetadata(report: Record<string, unknown>): boolean {
  if (report.generationTier === 'draft') return false;
  if (report.isDraft === true && report.qualityHold !== 'needs-revision') return false;

  const revision = report.reportRevision != null
    && typeof report.reportRevision === 'object'
    && !Array.isArray(report.reportRevision)
      ? report.reportRevision as Record<string, unknown>
      : null;
  if (!revision) return false;
  const nonEmpty = (value: unknown): boolean =>
    typeof value === 'string' && value.trim().length > 0;
  const hash = (value: unknown): boolean =>
    typeof value === 'string' && /^[a-f0-9]{64}$/.test(value);
  if (!nonEmpty(revision.sourcePath) || !hash(revision.sourceHash)) return false;

  const hasV2Identity = revision.schemaVersion === 'report-revision-ref-v1'
    || revision.documentPath != null
    || revision.documentHash != null
    || revision.revisionId != null;
  if (hasV2Identity && !(
    nonEmpty(revision.revisionId)
    && nonEmpty(revision.documentPath)
    && hash(revision.documentHash)
  )) return false;

  const decision = revision.deliveryDecision != null
    && typeof revision.deliveryDecision === 'object'
    && !Array.isArray(revision.deliveryDecision)
      ? revision.deliveryDecision as Record<string, unknown>
      : null;
  if (!decision || decision.canViewPrivate === false) return false;
  const reasons = Array.isArray(decision.reasons)
    ? decision.reasons.filter((reason): reason is Record<string, unknown> => (
        reason != null && typeof reason === 'object' && !Array.isArray(reason)
      ))
    : [];
  if (reasons.some(reason => [
    'unauthorized',
    'expired',
    'metadata-only',
    'body-unavailable',
  ].includes(String(reason.code)))) return false;

  const safeNonReadyCodes = new Set([
    'quality-held',
    'hard-gate-failed',
    'artifact-unavailable',
    'layout-pending',
    'layout-invalid',
    'projection-incomplete',
  ]);
  return decision.state === 'ready'
    || (
      decision.canViewPrivate === true
      && reasons.length > 0
      && reasons.every(reason => safeNonReadyCodes.has(String(reason.code)))
    );
}

/**
 * Hydrate the heads used by both Findings (updatedAt) and decision planning
 * (createdAt). Editing an older report must not leave the newer decision
 * carrier metadata-only. Historical bodies still load only when opened.
 */
export function selectEagerReportBodyIds(
  reports: readonly Record<string, unknown>[],
): ReadonlySet<string> {
  const newest = new Map<string, { id: string; timestamp: number }>();
  const newestBriefCarrier = new Map<string, { id: string; timestamp: number }>();
  const newestSavedCarrier = new Map<string, { id: string; timestamp: number }>();
  const newestCreated = new Map<string, { id: string; timestamp: number }>();
  const newestCreatedBriefCarrier = new Map<string, { id: string; timestamp: number }>();
  const newestCreatedSavedCarrier = new Map<string, { id: string; timestamp: number }>();

  const selectNewer = (
    selected: Map<string, { id: string; timestamp: number }>,
    key: string,
    candidate: { id: string; timestamp: number },
  ): void => {
    const current = selected.get(key);
    if (
      !current
      || candidate.timestamp > current.timestamp
      || (candidate.timestamp === current.timestamp && candidate.id.localeCompare(current.id) > 0)
    ) {
      selected.set(key, candidate);
    }
  };

  for (const report of reports) {
    const id = stringValue(report.id);
    if (!id) continue;
    const key = `${reportScopeKey(report)}|type:${reportTypeKey(report)}`;
    const candidate = { id, timestamp: reportTimestamp(report) };
    selectNewer(newest, key, candidate);
    if (canSupplyBriefFromMetadata(report)) {
      selectNewer(newestBriefCarrier, key, candidate);
    }
    if (canSupplySavedCalculationFromMetadata(report)) {
      selectNewer(newestSavedCarrier, key, candidate);
    }
    const createdAt = Date.parse(stringValue(report.createdAt) ?? '');
    if (Number.isFinite(createdAt)) {
      // Keep the first row on equal creation times, matching the stable sort
      // used by reportPlan and compositeScorer. Legacy undated rows retain
      // the existing updatedAt selection above.
      for (const selected of [
        newestCreated,
        ...(canSupplyBriefFromMetadata(report) ? [newestCreatedBriefCarrier] : []),
        ...(canSupplySavedCalculationFromMetadata(report) ? [newestCreatedSavedCarrier] : []),
      ]) {
        const current = selected.get(key);
        if (!current || createdAt > current.timestamp) {
          selected.set(key, { id, timestamp: createdAt });
        }
      }
    }
  }

  // Normally both maps select the same id. When a newer retry is held, keep
  // that attempt hydrated for the Analyses surface AND hydrate the last
  // passing carrier so the Stakeholder brief does not collapse on reload.
  return new Set(
    [...newest.values(), ...newestBriefCarrier.values(), ...newestSavedCarrier.values(),
      ...newestCreated.values(), ...newestCreatedBriefCarrier.values(),
      ...newestCreatedSavedCarrier.values()].map((entry) => entry.id),
  );
}

// ─── Site-scoped body hydration ──────────────────────────────────────────────
//
// The library lists every row the user can see, but a site page renders from
// the current heads of ONE site. Reading every site's heads before anything
// else cost a single site open ~50 bodies / ~34 MB and held `isReportsLoaded`
// (and with it the boot loader, Home, and Findings) until the whole portfolio
// landed. The open site's heads now gate the UI; the remaining heads warm up
// behind it in a narrow lane, and any site a surface needs sooner (a
// navigation, a composite recompute) is read on demand.
//
// Why the remaining heads are still read at all: portfolio surfaces mounted
// on every page read one body per site — the sidebar's screening
// recommendation, the deck's composite staleness check, the evidence
// readiness summaries persisted at startup. Leaving other sites metadata-only
// would blank those chips, drop every saved composite as stale and persist
// degraded summaries, so the policy is "open site first", not "open site
// only". For the same reason there is no LRU over resident bodies: those
// always-mounted consumers would re-request whatever it evicted.

/** The identity a surface uses to claim a site's report rows. */
export interface ReportSiteScope {
  id: string;
  name?: string | null;
  reportIds?: readonly string[] | null;
}

/**
 * Membership rule shared with the site landing page and the composite scorer:
 * a stamped `siteId`, the site's own `reportIds` list, or (legacy rows written
 * before site stamping) a whitespace-insensitive name match.
 */
export function reportMatchesSiteScope(
  report: Record<string, unknown>,
  scope: ReportSiteScope,
): boolean {
  return reportBelongsToSite(scope, { id: report.id, siteId: report.siteId, siteName: report.siteName });
}

/**
 * The eager policy above, restricted to the sites a surface is looking at.
 * Heads are still chosen across the WHOLE library (own + company rows, every
 * site) so the per-(site × type) selection is unchanged; only the set of rows
 * whose bodies get read is narrowed.
 */
export function selectEagerReportBodyIdsForSites(
  reports: readonly Record<string, unknown>[],
  scopes: readonly ReportSiteScope[],
): ReadonlySet<string> {
  if (scopes.length === 0) return new Set();
  const heads = selectEagerReportBodyIds(reports);
  const selected = new Set<string>();
  for (const report of reports) {
    const id = stringValue(report.id);
    if (!id || !heads.has(id)) continue;
    if (scopes.some((scope) => reportMatchesSiteScope(report, scope))) selected.add(id);
  }
  return selected;
}

/**
 * A site's Findings and decision surfaces may settle once its current heads
 * are resident or their read has settled (a failed read leaves an explicit
 * metadata-only row, exactly as the list pass always has). Rows that are
 * already full bodies (fixtures, mock mode, an opened reader) never hold a
 * site back, so a site with nothing left to read is never "pending".
 */
export function siteReportBodiesPending(
  siteReports: readonly Record<string, unknown>[],
  hydratedSiteIds: readonly string[] | null | undefined,
  siteId: string,
): boolean {
  if (hydratedSiteIds?.includes(siteId)) return false;
  if (siteReports.length === 0) return false;
  const heads = selectEagerReportBodyIds(siteReports);
  return siteReports.some((report) => {
    const id = stringValue(report.id);
    return id !== null
      && heads.has(id)
      && report._metadataOnly === true
      && typeof report.storagePath === 'string'
      && report.storagePath.length > 0;
  });
}

/**
 * Whether a site's inputs can be judged from the rows in hand. The composite
 * fingerprint deliberately tags metadata-only rows, so a portfolio surface
 * must not compare a saved composite against a site whose heads are still
 * on their way: it would read as stale on every load and trigger a recompute
 * that re-reads the whole portfolio. Until the site settles, its persisted
 * composite stands unless it is missing or explicitly epoch-stamped.
 */
export function siteReportBodiesSettled(
  site: ReportSiteScope,
  reports: readonly Record<string, unknown>[],
  hydratedSiteIds: readonly string[] | null | undefined,
): boolean {
  if (hydratedSiteIds?.includes(site.id)) return true;
  const siteRows = reports.filter((report) => reportMatchesSiteScope(report, site));
  return !siteReportBodiesPending(siteRows, hydratedSiteIds, site.id);
}

// ─── Row identity across list passes ─────────────────────────────────────────
//
// Every completing generation job re-runs the list pass. Handing each row a
// fresh object on every pass invalidated every memo keyed on `siteRag.reports`
// and re-read bodies that had not moved.

const REPORT_BODY_IDENTITY_FIELDS = ['id', 'siteId', 'storagePath', 'reportRevisionId', 'sourceHash'] as const;

/** The pointers a row declares must all match the resident body. */
function declaredIdentityMoved(metadata: Record<string, unknown>, resident: Record<string, unknown>): boolean {
  return REPORT_BODY_IDENTITY_FIELDS.some((key) => (
    Object.prototype.hasOwnProperty.call(metadata, key) && metadata[key] !== resident[key]
  ));
}

/** JSON-shaped structural equality; `undefined`-valued keys count as absent, as in serialisation. */
function structurallyEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (Array.isArray(a) || Array.isArray(b)) {
    return Array.isArray(a) && Array.isArray(b)
      && a.length === b.length
      && a.every((entry, index) => structurallyEqual(entry, b[index]));
  }
  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  const leftKeys = Object.keys(left).filter((key) => left[key] !== undefined);
  const rightKeys = Object.keys(right).filter((key) => right[key] !== undefined);
  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every((key) => (
    Object.prototype.hasOwnProperty.call(right, key) && structurallyEqual(left[key], right[key])
  ));
}

/**
 * A re-listed metadata row that says nothing new keeps the object already in
 * the store, so memoised selectors keyed on row identity stay warm across the
 * list passes that every completing generation job triggers.
 */
export function reuseUnchangedMetadataRow(
  next: SiteRagReport,
  existing: SiteRagReport | undefined,
): SiteRagReport {
  if (!existing || next._metadataOnly !== true || existing._metadataOnly !== true) return next;
  return structurallyEqual(next, existing) ? existing : next;
}

/**
 * Skip the body read when the store already holds a full body for the exact
 * artifact the row points at. Immutable revisions are content-addressed, so a
 * matching pointer set IS the same body. A mutable legacy object (flat
 * `users/{uid}/reports/{id}.json`) changes under a stable path, so it is
 * reused only while the row's `updatedAt` is the one listed when the body was
 * read. Authoritative Firestore fields committed since are folded in.
 */
export function reuseHydratedReportBody(
  metadata: Record<string, unknown>,
  existing: SiteRagReport | undefined,
  previousMetadata?: Record<string, unknown> | null,
  options?: { requireFullBody?: boolean },
): SiteRagReport | null {
  if (!existing || existing._metadataOnly === true) return null;
  if (options?.requireFullBody && isPortfolioSlimReportBody(existing)) return null;
  if (typeof metadata.storagePath !== 'string' || !metadata.storagePath) return null;
  const resident = existing as unknown as Record<string, unknown>;
  if (declaredIdentityMoved(metadata, resident)) return null;
  if (!isImmutableRevisionReport(metadata)
    && (!previousMetadata || previousMetadata.updatedAt !== metadata.updatedAt)) return null;
  const authoritative = pickAuthoritativeReportMetadata(metadata);
  const changed = Object.keys(authoritative)
    .some((key) => !structurallyEqual(authoritative[key], resident[key]));
  return changed
    ? {
        ...existing,
        ...authoritative,
        _metadataOnly: false,
        ...(isPortfolioSlimReportBody(existing) ? { _portfolioSlimBody: true } : {}),
      } as unknown as SiteRagReport
    : existing;
}

/** Ordered, fail-fast async map with a hard concurrency ceiling. */
export async function mapWithConcurrency<T, R>(
  values: readonly T[],
  concurrency: number,
  mapper: (value: T, index: number) => Promise<R>,
): Promise<R[]> {
  if (values.length === 0) return [];
  const workerCount = Math.max(1, Math.min(Math.floor(concurrency), values.length));
  const results = new Array<R>(values.length);
  let nextIndex = 0;

  await Promise.all(Array.from({ length: workerCount }, async () => {
    while (nextIndex < values.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await mapper(values[index] as T, index);
    }
  }));

  return results;
}
